import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TerminalIcon from '@mui/icons-material/Terminal';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { AppArt, AppArtBucket } from '../types';

const HEX = /^#[0-9A-Fa-f]{6}$/;

type Desenho = 'padrao' | 'agente';

const DESENHOS: { valor: Desenho; rotulo: string; ajuda: string }[] = [
  {
    valor: 'padrao',
    rotulo: 'Modelo padrão CTTX',
    ajuda: 'o desenho aprovado, sempre no mesmo azul — só a logo da rede muda',
  },
  {
    valor: 'agente',
    rotulo: 'Desenho novo',
    ajuda: 'o Claude desenha uma peça nova com a cor da rede',
  },
];

/**
 * O comando do gerador de PLACAS desta rede, pronto para colar no terminal.
 *
 * Mesma razão do "Gerar modelos": o `placa.mjs` roda na máquina de quem opera
 * (Playwright, e no desenho novo a CLI do Claude), então a tela não executa
 * nada — ela monta o comando com o chainId e as opções. E também sem `cd`: roda
 * de dentro do checkout do `pontuax-art-generator`.
 *
 * O padrão é o MODELO FIXO da CTTX (`--padrao`): o desenho aprovado, no azul
 * dele, com só a logo da rede trocada. O desenho novo continua a um clique,
 * para a rede que quer uma placa própria.
 *
 * O que o botão precisa carregar, porque o script não perdoa:
 *
 * - SEM `--subir` o `placa.mjs` só grava em `out/` e não envia nada. Quem clica
 *   aqui espera a placa aparecer nesta seção, então a flag vem ligada e
 *   desligar é a exceção ("gerar para olhar").
 * - O `--padrao` IGNORA `--motor`, `--cor` e `--artes`. Eles nem aparecem no
 *   modelo fixo, para o comando não sugerir que valem.
 * - No desenho novo o script PARA se a rede não tem cor no cadastro nem `--cor`.
 *   Nos dois, para sem logo. Melhor dizer isso antes do terminal.
 */
const PlateCommandButton = () => {
  const record = useRecordContext<AppArt>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [desenho, setDesenho] = useState<Desenho>('padrao');
  const [artes, setArtes] = useState('');
  const [cor, setCor] = useState('');
  /* Desligada por padrão, ao contrário do gerador de fundo. Aqui a avaliação é
     a barreira de contraste da logo contra o fundo — sem ela sai peça com a
     marca do cliente sumindo. As barreiras de HTML e de slot valem sempre. */
  const [semAvaliacao, setSemAvaliacao] = useState(false);
  const [subir, setSubir] = useState(true);

  if (!record) { return null; }

  const padrao = desenho === 'padrao';
  const semLogo = !(record.files || []).some(file => file.bucket === AppArtBucket.logo);
  const corDaRede = record.color && HEX.test(record.color) ? record.color.toUpperCase() : null;
  const corDigitada = cor.trim();
  const corInvalida = !padrao && !!corDigitada && !HEX.test(corDigitada);
  const semCor = !padrao && !corDaRede && !HEX.test(corDigitada);

  const flags = [
    `--chain ${record.chainId}`,
    // No desenho novo o motor é fixo, pelo mesmo motivo do "Gerar modelos": é o
    // Claude que desenha, e o determinístico fica no script como rede de proteção.
    padrao ? '--padrao' : '--motor agente',
    !padrao && artes ? `--artes ${artes}` : '',
    // Entre aspas: `#` no começo de palavra é comentário no bash, e a cor
    // sumiria do comando sem ninguém ver.
    !padrao && HEX.test(corDigitada) ? `--cor '${corDigitada.toUpperCase()}'` : '',
    semAvaliacao ? '--sem-avaliacao' : '',
    subir ? '--subir' : '',
  ].filter(Boolean).join(' ');

  const command = `node src/placa.mjs ${flags}`;

  const copy = () => {
    copyToClipboard(command);
    notify('Comando copiado — cole no terminal.', { type: 'info' });
  };

  return (
    <>
      <Tooltip title="Monta o comando do gerador de placas para esta rede">
        <Button size="small" variant="outlined" startIcon={<TerminalIcon />} onClick={() => setOpen(true)}>
          Gerar placas
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar placas de posto — {record.chainName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rode dentro do <code>pontuax-art-generator</code>.{' '}
            {padrao
              ? 'Ele coloca a logo da rede no modelo aprovado da CTTX — o desenho e o azul são sempre os mesmos — e leva alguns segundos.'
              : 'Ele usa a CLI do Claude autenticada na sua máquina para desenhar uma peça nova com a logo e a cor da rede. Cada peça leva por volta de 13 minutos.'}
            {' '}Os espaços do QR e do código saem vazios: quem preenche é esta tela, com os
            códigos do mapa de pista.
          </Typography>

          {semLogo && (
            <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
              Esta rede não tem nenhuma logo no material. O gerador para sem ela —
              suba a logo antes de rodar.
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              select
              size="small"
              label="Desenho"
              value={desenho}
              onChange={event => setDesenho(event.target.value as Desenho)}
              sx={{ minWidth: 240 }}
              helperText={DESENHOS.find(d => d.valor === desenho)?.ajuda}
            >
              {DESENHOS.map(d => (
                <MenuItem key={d.valor} value={d.valor}>{d.rotulo}</MenuItem>
              ))}
            </TextField>

            {!padrao && (
              <TextField
                select
                size="small"
                label="Quantas placas"
                value={artes}
                onChange={event => setArtes(event.target.value)}
                sx={{ minWidth: 180 }}
                helperText="em branco = 1"
              >
                <MenuItem value="">1 (padrão)</MenuItem>
                {[2, 3, 4].map(n => (
                  <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                ))}
              </TextField>
            )}

            {!padrao && (
              <TextField
                size="small"
                label="Cor (--cor)"
                placeholder="#1A2B3C"
                value={cor}
                onChange={event => setCor(event.target.value)}
                sx={{ minWidth: 200 }}
                error={corInvalida || semCor}
                helperText={corInvalida
                  ? 'use o formato #RRGGBB'
                  : corDaRede
                    ? `em branco = cor da rede (${corDaRede})`
                    : 'a rede não tem cor no cadastro — obrigatória'}
              />
            )}

            <Box>
              <FormControlLabel
                control={<Checkbox checked={subir} onChange={(_, v) => setSubir(v)} />}
                label="Subir para esta tela (--subir)"
              />
              <Tooltip title="Marcado: aceita a peça sem medir se a logo casa com o fundo atrás dela. É essa barreira que impede a marca do cliente de sumir no desenho. As barreiras de HTML e dos espaços do QR e do código continuam valendo.">
                <FormControlLabel
                  control={<Checkbox checked={semAvaliacao} onChange={(_, v) => setSemAvaliacao(v)} />}
                  label="Pular a barreira de contraste da logo (--sem-avaliacao)"
                />
              </Tooltip>
            </Box>
          </Box>

          {!subir && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Sem <code>--subir</code> a placa fica só em <code>out/</code> na sua
              máquina e não aparece nesta seção.
            </Typography>
          )}

          <Box
            sx={{
              p: 2, borderRadius: 1, backgroundColor: 'grey.900', color: 'grey.100',
              fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {command}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            O <code>--subir</code> envia para a API do <code>API_URL</code> do <code>.env</code> do
            gerador — confira se ele aponta para o mesmo ambiente desta tela.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={copy}
            disabled={semCor || corInvalida}
          >
            Copiar comando
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlateCommandButton;
